import React, { useEffect, useState, useCallback } from "react";
import { Loader2, AlertTriangle, BarChart3, Clock, ShieldCheck, Target } from "lucide-react";
import { api } from "../lib/api.js";

function formatHours(h) {
  if (h == null || isNaN(h)) return "—";
  if (h < 1) return `${Math.max(1, Math.round(h * 60))} min`;
  if (h < 24) return `${h.toFixed(1)} h`;
  const days = Math.floor(h / 24);
  const rem = Math.round(h - days * 24);
  return rem > 0 ? `${days}d ${rem}h` : `${days}d`;
}

function BlockerBar({ b, maxAvg }) {
  const pct = maxAvg > 0 ? Math.min(100, Math.round((b.avg_hours / maxAvg) * 100)) : 0;
  return (
    <div className="flex items-center gap-3 py-1.5" data-testid={`analytics-blocker-${b.state}`}>
      <div className="w-48 truncate text-sm text-[#1A1A2E]" title={b.state}>{b.state}</div>
      <div className="flex-1 bg-[#E2E8F0] rounded-full h-2 overflow-hidden">
        <div className="bg-[#1F3864] h-full rounded-full" style={{ width: `${pct}%` }} />
      </div>
      <div className="w-20 text-right text-xs font-mono text-[#1A1A2E]">{formatHours(b.avg_hours)}</div>
      <div className="w-16 text-right text-[10px] text-[#6C757D]">n={b.sample_count}</div>
    </div>
  );
}

function EntityCard({ title, entity, data, testid }) {
  if (!data) return null;
  const top = data.top_blocking || [];
  const maxAvg = top.length ? Math.max(...top.map((b) => b.avg_hours)) : 0;
  return (
    <div className="bg-white border border-gray-200 rounded-md p-4" data-testid={`analytics-card-${entity}`}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-[#1A1A2E] flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-[#1F3864]" /> {title}
        </h3>
        <span className="text-[10px] uppercase tracking-widest text-[#6C757D]">
          {data.total_transitions} transições · {data.all_states.length} estados
        </span>
      </div>
      {top.length === 0 ? (
        <div className="text-xs text-[#94A3B8] italic" data-testid={`analytics-empty-${entity}`}>
          Sem transições suficientes para calcular dwell time.
        </div>
      ) : (
        <div className="space-y-1" data-testid={testid}>
          <div className="flex items-center gap-3 text-[10px] uppercase tracking-widest text-[#6C757D] mb-1">
            <div className="w-48">Estado</div>
            <div className="flex-1">Tempo médio</div>
            <div className="w-20 text-right">Média</div>
            <div className="w-16 text-right">Amostra</div>
          </div>
          {top.map((b) => <BlockerBar key={b.state} b={b} maxAvg={maxAvg} />)}
        </div>
      )}
    </div>
  );
}

function WarrantyRejectionsCard({ data }) {
  if (!data) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-md p-4" data-testid="analytics-card-warranty-rejections">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-[#1A1A2E] flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-[#DC3545]" /> Garantias rejeitadas
        </h3>
        <span className="text-[10px] uppercase tracking-widest text-[#6C757D]">
          {data.total_rejections} total
        </span>
      </div>
      {data.total_rejections === 0 ? (
        <div className="text-xs text-[#94A3B8] italic" data-testid="analytics-rejections-empty">
          Sem garantias com estado “Não aprovado”.
        </div>
      ) : (
        <>
          <div className="text-[10px] uppercase tracking-widest text-[#6C757D] mb-1">Por marca</div>
          <ul className="space-y-1 mb-3">
            {data.by_brand.map((b) => (
              <li key={b.brand} className="flex items-center justify-between text-sm" data-testid={`analytics-rejection-brand-${b.brand}`}>
                <span>{b.brand}</span>
                <span className="font-mono font-semibold text-[#DC3545]">{b.count}</span>
              </li>
            ))}
          </ul>
          <div className="text-[10px] uppercase tracking-widest text-[#6C757D] mb-1 mt-3">Detalhe por marca + tipo</div>
          <table className="w-full text-sm" data-testid="analytics-rejection-table">
            <thead className="text-[10px] uppercase tracking-widest text-[#6C757D]">
              <tr><th className="text-left py-1">Marca</th><th className="text-left">Tipo</th><th className="text-right">Total</th></tr>
            </thead>
            <tbody>
              {data.by_brand_and_type.map((r, i) => (
                <tr key={i} className="border-t border-gray-100">
                  <td className="py-1">{r.brand}</td>
                  <td>{r.claim_type}</td>
                  <td className="text-right font-mono">{r.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}

export default function AnalyticsPanel() {
  const [data, setData] = useState(null);
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [s, k] = await Promise.all([
        api.get("/analytics/summary"),
        api.get("/analytics/kpis"),
      ]);
      setData(s.data);
      setKpis(k.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Falha ao carregar.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="p-8 text-center text-sm text-[#6C757D]" data-testid="analytics-loading">
        <Loader2 className="w-4 h-4 inline-block animate-spin mr-2" /> A calcular dwell time…
      </div>
    );
  }
  if (error) {
    return <div className="p-8 text-sm text-[#DC3545]" data-testid="analytics-error">{error}</div>;
  }
  if (!data) return null;

  return (
    <div className="space-y-4" data-testid="analytics-panel">
      <KpiSection kpis={kpis} />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <EntityCard title="Obras de Manutenção" entity="som" data={data.som} testid="analytics-som-table" />
        <EntityCard title="Encomendas de Peças" entity="pec" data={data.pec} testid="analytics-pec-table" />
        <EntityCard title="Classificações de Peças" entity="pcp" data={data.pcp} testid="analytics-pcp-table" />
        <EntityCard title="Garantias" entity="swc" data={data.swc} testid="analytics-swc-table" />
        <div className="lg:col-span-2">
          <WarrantyRejectionsCard data={data.warranty_rejections} />
        </div>
      </div>
    </div>
  );
}

// ===== KPI section =====

function Stat({ label, value, suffix, icon, tone, sub, testid }) {
  const tones = {
    blue: "border-l-[#1F3864] text-[#1F3864]",
    green: "border-l-[#28A745] text-[#28A745]",
    amber: "border-l-[#FFC107] text-[#856404]",
    red: "border-l-[#DC3545] text-[#DC3545]",
  };
  return (
    <div className={`bg-white border border-gray-200 border-l-4 ${tones[tone] || tones.blue} rounded-md p-5`} data-testid={testid}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] uppercase tracking-widest text-[#6C757D]">{label}</span>
        {icon}
      </div>
      <div className="flex items-baseline gap-1.5">
        <span className="text-3xl font-bold font-mono text-[#1A1A2E]">{value ?? "—"}</span>
        {suffix && <span className="text-sm text-[#6C757D]">{suffix}</span>}
      </div>
      {sub && <div className="text-xs text-[#6C757D] mt-1">{sub}</div>}
    </div>
  );
}

function KpiSection({ kpis }) {
  if (!kpis) return null;
  const monthLabel = new Date(kpis.month_window.start).toLocaleDateString("pt-PT", { month: "long", year: "numeric" });
  const tatBrands = kpis.tat.by_brand || [];
  const warrBrands = kpis.warranty_approval.by_brand || [];
  return (
    <div className="bg-[#F4F6F8] rounded-md p-4" data-testid="kpis-section">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-[#1A1A2E]">
          KPIs — <span className="text-[#1F3864]">{monthLabel}</span>
        </h3>
        <span className="text-[10px] uppercase tracking-widest text-[#6C757D]">mês corrente</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <Stat
          label="TAT médio"
          value={kpis.tat.overall_avg_business_days}
          suffix="dias úteis"
          icon={<Clock className="w-4 h-4 text-[#1F3864]" />}
          tone="blue"
          sub={`${kpis.tat.closed_soms_total} obra(s) encerrada(s)`}
          testid="kpi-tat"
        />
        <Stat
          label="% dentro do SLA"
          value={kpis.sla.on_time_pct}
          suffix="%"
          icon={<Target className="w-4 h-4 text-[#28A745]" />}
          tone={kpis.sla.on_time_pct == null ? "blue" : kpis.sla.on_time_pct >= 80 ? "green" : kpis.sla.on_time_pct >= 50 ? "amber" : "red"}
          sub={`${kpis.sla.on_time_count}/${kpis.sla.denominator} · SLA defeito ${kpis.sla.default_business_days} dias`}
          testid="kpi-sla"
        />
        <Stat
          label="Garantias aprovadas"
          value={kpis.warranty_approval.overall_pct}
          suffix="%"
          icon={<ShieldCheck className="w-4 h-4 text-[#1F3864]" />}
          tone={kpis.warranty_approval.overall_pct == null ? "blue" : kpis.warranty_approval.overall_pct >= 75 ? "green" : kpis.warranty_approval.overall_pct >= 50 ? "amber" : "red"}
          sub={`${kpis.warranty_approval.approved_count}/${kpis.warranty_approval.decided_count} decididas`}
          testid="kpi-warranty"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="bg-white border border-gray-200 rounded-md p-3" data-testid="kpi-tat-by-brand">
          <div className="text-[10px] uppercase tracking-widest text-[#6C757D] mb-2">TAT por marca</div>
          {tatBrands.length === 0 ? (
            <div className="text-xs italic text-[#94A3B8]">Sem obras encerradas este mês.</div>
          ) : (
            <table className="w-full text-xs">
              <thead className="text-[10px] uppercase tracking-widest text-[#6C757D]">
                <tr><th className="text-left py-1">Marca</th><th className="text-right">Média</th><th className="text-right">SLA</th><th className="text-right">No prazo</th><th className="text-right">Amostra</th></tr>
              </thead>
              <tbody>
                {tatBrands.map((b) => (
                  <tr key={b.brand} className="border-t border-gray-100" data-testid={`kpi-tat-row-${b.brand}`}>
                    <td className="py-1">{b.brand}</td>
                    <td className="text-right font-mono">{b.avg_business_days}d</td>
                    <td className="text-right font-mono text-[#6C757D]">{b.sla_business_days}d</td>
                    <td className={`text-right font-semibold ${b.on_time_pct >= 80 ? "text-[#28A745]" : b.on_time_pct >= 50 ? "text-[#856404]" : "text-[#DC3545]"}`}>{b.on_time_pct == null ? "—" : `${b.on_time_pct}%`}</td>
                    <td className="text-right font-mono text-[#6C757D]">{b.sample_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="bg-white border border-gray-200 rounded-md p-3" data-testid="kpi-warranty-by-brand">
          <div className="text-[10px] uppercase tracking-widest text-[#6C757D] mb-2">Garantias por marca</div>
          {warrBrands.length === 0 ? (
            <div className="text-xs italic text-[#94A3B8]">Sem decisões de garantia este mês.</div>
          ) : (
            <table className="w-full text-xs">
              <thead className="text-[10px] uppercase tracking-widest text-[#6C757D]">
                <tr><th className="text-left py-1">Marca</th><th className="text-right">Aprovadas</th><th className="text-right">Total</th><th className="text-right">Taxa</th></tr>
              </thead>
              <tbody>
                {warrBrands.map((b) => (
                  <tr key={b.brand} className="border-t border-gray-100" data-testid={`kpi-warranty-row-${b.brand}`}>
                    <td className="py-1">{b.brand}</td>
                    <td className="text-right font-mono">{b.approved}</td>
                    <td className="text-right font-mono text-[#6C757D]">{b.total}</td>
                    <td className={`text-right font-semibold ${b.approval_pct >= 75 ? "text-[#28A745]" : b.approval_pct >= 50 ? "text-[#856404]" : "text-[#DC3545]"}`}>{b.approval_pct}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
